import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { useForm, useFieldArray, useController } from 'react-hook-form';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { styled } from '@mui/material/styles';

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  margin: '24px 0 0',
  border: '1px solid #E1E1E1',
  borderRadius: '8px',
  width: '100%',
  maxWidth: '100%',
  boxShadow: 'none',
  '&:before': {
    display: 'none',
  },
  marginTop: '24px !important',

  '& .MuiAccordionSummary-content': {
    margin: '0 !important',
  },
  '& .MuiAccordionDetails-root': {
    padding: 0,
  },
}));

const NerisTypesForm = ({ initialData, groups, onSubmit, loading }) => {
  const {
    control,
    handleSubmit,
    formState: { isDirty },
  } = useForm({
    values: {
      types: initialData,
    },
  });

  const { fields } = useFieldArray({
    control,
    name: 'types',
  });

  const handleFormSubmit = (data) => {
    onSubmit(data.types);
  };

  const groupedFields = useMemo(() => {
    return fields.reduce((acc, field, index) => {
      if (!acc[field.group_id]) acc[field.group_id] = [];
      acc[field.group_id].push({ ...field, index });
      return acc;
    }, {});
  }, [fields]);

  const getGroupName = (groupId) => {
    return groups.find((group) => group.id === groupId)?.neris_name || groupId;
  };

  return (
    <>
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        {Object.entries(groupedFields).map(([groupId, groupFields]) => (
          <StyledAccordion key={groupId}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                p: 3,
              }}
            >
              <Typography className="title">
                {getGroupName(Number(groupId))}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box
                sx={{
                  pl: 3,
                  pr: 3,
                  mb: 2,
                }}
              >
                {/* Header */}
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '50px 1fr 1fr',
                    gap: 2,
                    mb: 2,
                    alignItems: 'center',
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Number
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Type
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Custom name
                  </Typography>
                </Box>

                {groupFields.map((field) => (
                  <Row
                    key={field.id}
                    number={field.number}
                    type={field.type}
                    index={field.index}
                    control={control}
                  />
                ))}
              </Box>
            </AccordionDetails>
          </StyledAccordion>
        ))}

        <Button
          type="submit"
          variant="contained"
          disabled={loading || !isDirty}
          sx={{ minWidth: 100, mt: 3 }}
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : 'Save'}
        </Button>
      </form>
    </>
  );
};

const Row = React.memo(({ number, type, index, control }) => {
  const { field } = useController({
    name: `types.${index}.name`,
    control,
  });

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '50px 1fr 1fr',
        gap: 2,
        mb: 2,
        alignItems: 'center',
        py: 1,
      }}
    >
      <Typography variant="body1" sx={{ fontWeight: 500 }}>
        {number}
      </Typography>
      <Typography variant="body1" sx={{ fontWeight: 500 }}>
        {type}
      </Typography>
      <TextField
        {...field}
        placeholder="Write"
        variant="outlined"
        size="small"
        fullWidth
      />
    </Box>
  );
});

export default NerisTypesForm;
